import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { useVault, useVaultStatistics } from '@/hooks/useVault';
import { VaultStatus, VaultEncryptionMode } from '@/types/vault.types';
import { useVaultKeyStore } from '@/store/vaultKeyStore';
import {
  Calendar,
  FileText,
  Key,
  Lock,
  LockOpen,
  Settings,
  Upload,
  Users,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useAuthStore } from '@/store/auth.store';
import {
  AuditLogsTab,
  DocumentsTab,
  FolderMembersTab,
  InitializeVaultModal,
  SelfUploadDocumentModal,
  UnlockVaultModal,
  VaultStatisticsTab,
  VaultUserLayout,
} from '../components';
import { VaultEntityType } from '@/types/vault.types';

export const VaultDetailPage = () => {
  const { t } = useTranslation();
  const { vaultId } = useParams<{ vaultId: string }>();
  const { user } = useAuthStore();
  const [showInitializeModal, setShowInitializeModal] = useState(false);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [showSelfUploadModal, setShowSelfUploadModal] = useState(false);

  // Gestionnaire → retour navigateur, vault_user → /my-vaults
  const isVaultUser = user?.vaultOnlyAccess;
  const backPath = isVaultUser ? '/my-vaults' : undefined;

  // Utiliser enabled: !!vaultId pour éviter requête inutile
  const { data: vault, isLoading, error } = useVault(vaultId || '', !!vaultId);
  const { data: statistics } = useVaultStatistics(vaultId || '');

  // Vault lock state management
  const isUnlocked = useVaultKeyStore((state) =>
    vaultId ? state.unlockedVaults.has(vaultId) : false
  );

  if (isLoading) {
    return (
      <VaultUserLayout
        title={t('vault.detail.title', 'Détail du coffre-fort')}
        showBackButton
        backPath={backPath}
      >
        <div className="space-y-6">
          <LoadingSkeleton className="h-8 w-64" />
          <LoadingSkeleton className="h-12 w-full" />
          <LoadingSkeleton className="h-96" />
        </div>
      </VaultUserLayout>
    );
  }

  if (error || !vault) {
    return (
      <VaultUserLayout
        title={t('vault.detail.title', 'Détail du coffre-fort')}
        showBackButton
        backPath={backPath}
      >
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="mb-4 rounded-full bg-error-100 p-3">
            <XCircle className="h-6 w-6 text-error-600" />
          </div>
          <h3 className="mb-2 text-lg font-semibold text-neutral-900">
            {t('vault.error.notFound', 'Coffre-fort introuvable')}
          </h3>
          <p className="text-sm text-neutral-600">
            {t('vault.error.load', 'Impossible de charger les détails du coffre')}
          </p>
        </div>
      </VaultUserLayout>
    );
  }

  const getStatusConfig = (status: VaultStatus) => {
    switch (status) {
      case VaultStatus.ACTIVE:
        return {
          className: 'bg-success-100 text-success-700 border-success-200',
          label: t('vault.status.active', 'Actif'),
        };
      case VaultStatus.PENDING:
        return {
          className: 'bg-warning-100 text-warning-700 border-warning-200',
          label: t('vault.status.pending', 'En attente'),
        };
      case VaultStatus.SUSPENDED:
        return {
          className: 'bg-error-100 text-error-700 border-red-200',
          label: t('vault.status.suspended', 'Suspendu'),
        };
      default:
        return {
          className: 'bg-neutral-100 text-neutral-700 border-neutral-200',
          label: status,
        };
    }
  };

  const statusConfig = getStatusConfig(vault.status);
  const isServerSide = vault.encryptionMode === VaultEncryptionMode.SERVER_SIDE;

  // Build header actions for the layout
  const headerActions = (
    <div className="flex items-center gap-2">
      {/* Status Badge */}
      <span
        className={`rounded-full px-3 py-1 text-xs font-medium ${statusConfig.className}`}
      >
        {statusConfig.label}
      </span>

      {/* Encryption Status */}
      {isServerSide ? (
        <span className="cursor-help rounded-full bg-success-50 px-3 py-1 text-xs font-medium text-success-700">
          {t('vault.serverEncrypted', 'Chiffré (serveur)')}
        </span>
      ) : vault.encryptionParams ? (
        <span className="cursor-help rounded-full bg-success-50 px-3 py-1 text-xs font-medium text-success-700">
          {t('vault.encrypted', 'Chiffré')}
        </span>
      ) : (
        <span className="cursor-help rounded-full bg-warning-50 px-3 py-1 text-xs font-medium text-warning-700">
          {t('vault.notInitialized', 'Non initialisé')}
        </span>
      )}

      {/* Lock Status - Not shown for SERVER_SIDE vaults (always accessible) */}
      {!isServerSide && vault.encryptionParams && (
        isUnlocked ? (
          <span className="flex items-center gap-1 rounded-full bg-green-50 px-3 py-1 text-xs font-medium text-green-700">
            <LockOpen className="h-3 w-3" />
            {t('vault.unlocked', 'Déverrouillé')}
          </span>
        ) : (
          <span className="flex items-center gap-1 rounded-full bg-neutral-100 px-3 py-1 text-xs font-medium text-neutral-700">
            <Lock className="h-3 w-3" />
            {t('vault.locked', 'Verrouillé')}
          </span>
        )
      )}
    </div>
  );

  return (
    <VaultUserLayout
      title={vault.name}
      subtitle={vault.description}
      showBackButton
      backPath={backPath}
      headerActions={headerActions}
    >
      {/* Action Buttons */}
      <div className="mb-6 flex flex-wrap items-center gap-2">
        {!isServerSide && !vault.encryptionParams && vault.status === VaultStatus.PENDING && (
          <button
            onClick={() => setShowInitializeModal(true)}
            className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700"
          >
            <Key className="h-4 w-4" />
            {t('vault.initialize', 'Initialiser')}
          </button>
        )}
        {isServerSide && vault.status === VaultStatus.ACTIVE && (
          /* SERVER_SIDE vaults: no unlock needed, direct upload */
          <button
            onClick={() => setShowSelfUploadModal(true)}
            className="flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-purple-700"
          >
            <Upload className="h-4 w-4" />
            {t('vault.selfUpload.button', 'Ajouter un document')}
          </button>
        )}
        {!isServerSide && vault.encryptionParams && vault.status === VaultStatus.ACTIVE && (
          <>
            {!isUnlocked && (
              <button
                onClick={() => setShowUnlockModal(true)}
                className="flex items-center gap-2 rounded-lg border border-primary-300 bg-primary-50 px-4 py-2 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-100"
              >
                <LockOpen className="h-4 w-4" />
                {t('vault.unlock', 'Déverrouiller')}
              </button>
            )}
            {isUnlocked &&
              vault.accessType === 'direct' &&
              vault.entityType === VaultEntityType.EMPLOYEE && (
                <button
                  onClick={() => setShowSelfUploadModal(true)}
                  className="flex items-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-purple-700"
                >
                  <Upload className="h-4 w-4" />
                  {t('vault.selfUpload.button', 'Ajouter un document')}
                </button>
              )}
          </>
        )}
      </div>

      {/* Tabs */}
      <Tabs defaultValue="documents" className="w-full">
        <TabsList className="mb-0 w-full justify-start overflow-x-auto">
          <TabsTrigger value="documents" className="gap-2">
            <FileText className="h-4 w-4" />
            {t('vault.tabs.documents', 'Documents')}
            {statistics?.totalDocuments !== undefined && (
              <span className="ml-1 rounded-full bg-neutral-100 px-2 py-0.5 text-xs text-neutral-600">
                {statistics.totalDocuments}
              </span>
            )}
          </TabsTrigger>
          {isServerSide && vault.entityType === VaultEntityType.FOLDER && (
            <TabsTrigger value="members" className="gap-2">
              <Users className="h-4 w-4" />
              {t('vault.tabs.members', 'Membres')}
            </TabsTrigger>
          )}
          <TabsTrigger value="audit" className="gap-2">
            <Calendar className="h-4 w-4" />
            {t('vault.tabs.audit', 'Historique')}
          </TabsTrigger>
          <TabsTrigger value="statistics" className="gap-2">
            <Settings className="h-4 w-4" />
            {t('vault.tabs.statistics', 'Statistiques')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="documents">
          <div className="rounded-lg border border-neutral-200 bg-white p-6">
            <DocumentsTab vaultId={vault.uuid} />
          </div>
        </TabsContent>

        {isServerSide && vault.entityType === VaultEntityType.FOLDER && (
          <TabsContent value="members">
            <div className="rounded-lg border border-neutral-200 bg-white">
              <FolderMembersTab vaultId={vault.uuid} />
            </div>
          </TabsContent>
        )}

        <TabsContent value="audit">
          <AuditLogsTab vaultId={vault.uuid} />
        </TabsContent>

        <TabsContent value="statistics">
          <VaultStatisticsTab vaultId={vault.uuid} />
        </TabsContent>
      </Tabs>

      {/* Modals */}
      {showInitializeModal && (
        <InitializeVaultModal
          vaultId={vault.uuid}
          isOpen={showInitializeModal}
          onClose={() => setShowInitializeModal(false)}
        />
      )}

      {showUnlockModal && (
        <UnlockVaultModal
          vaultId={vault.uuid}
          isOpen={showUnlockModal}
          onClose={() => setShowUnlockModal(false)}
          unlockOnly
        />
      )}

      {showSelfUploadModal && (
        <SelfUploadDocumentModal
          vaultId={vault.uuid}
          vaultName={vault.name}
          isOpen={showSelfUploadModal}
          onClose={() => setShowSelfUploadModal(false)}
        />
      )}
    </VaultUserLayout>
  );
};
